import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AutoComplete,
  Avatar,
  Button,
  Card,
  Drawer,
  Empty,
  List,
  message,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FilterOutlined,
  MessageOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { startPrivateConversation } from "services/chatService";
import { olreadiaService } from "services/olreadiaService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { normalizeProfile, toList } from "./professionalProfileUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const PAGE_SIZE = 20;

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeSearchValue = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

const getProfessionSectorCode = (sector) => {
  if (!sector) return "GENERAL";
  if (typeof sector === "string") return sector || "GENERAL";
  return sector.code || "GENERAL";
};

const getProfessionSectorName = (sector) => {
  if (!sector) return "General";
  if (typeof sector === "string") return titleCase(sector);
  return sector.name || titleCase(sector.code);
};

const talentName = (profile) => {
  const preferredName = [
    profile?.preferredFirstName || profile?.firstName,
    profile?.middleName,
    profile?.preferredLastName || profile?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    preferredName ||
    profile?.displayName ||
    profile?.name ||
    profile?.fullName ||
    profile?.profileName ||
    profile?.title ||
    "Talent profile"
  );
};

const profileTitle = (profile) => {
  if (profile?.profileName?.trim()) return profile.profileName.trim();
  const sector = profile?.linesOfProfession?.[0];
  const sectorCode = getProfessionSectorCode(sector);
  return sectorCode === "GENERAL"
    ? "General Professional Profile"
    : `${getProfessionSectorName(sector)} Professional Profile`;
};

const lineLabels = (profile) =>
  (profile?.linesOfProfession || [])
    .map((sector) => getProfessionSectorName(sector))
    .filter(Boolean);

const locationLabels = (profile) =>
  (profile?.locations || [])
    .map((location) => {
      if (location?.open) return "Open to locations";
      return [location?.city, location?.state, location?.county, location?.country].filter(Boolean).join(", ");
    })
    .filter(Boolean);

const talentPhoto = (profile) =>
  profile?.profilePhotoUrl || profile?.photoUrl || profile?.profilePhoto || "";

const talentVerified = (profile) => Boolean(profile?.isVerified ?? profile?.isVersified);

const profileOwnerId = (profile) =>
  String(
    profile?.userProfileId ||
      profile?.userId ||
      profile?.helperId ||
      profile?.ownerId ||
      profile?.createdBy ||
      "",
  ).trim();

const itemTitle = (item, fallback = "Item") => {
  if (typeof item === "string") return item;
  return item?.role || item?.name || item?.institution || item?.caption || item?.title || item?.url || item?.id || fallback;
};

const itemMeta = (item) => {
  if (!item || typeof item === "string") return "";
  return [
    item.organization,
    item.relationship,
    item.program,
    item.educationLevel || item.degree,
    item.location,
    item.issuer,
    item.phone,
    item.email,
    item.county,
    item.country,
    item.open === false ? "Limited" : item.open === true ? "Open" : "",
  ].filter(Boolean).join(" • ");
};

const serviceLabel = (service) =>
  typeof service === "string"
    ? service
    : service?.name || service?.briefName || service?.code || service?.id || "Service";

export const TalentProfileDrawer = ({
  actorId,
  chatStarting = false,
  detailsError = "",
  detailsLoading = false,
  onChat,
  onClose,
  onOpenProfileDetails,
  onShare,
  open,
  selectedProfile,
  selectedProfileDetails,
}) => {
  const selectedLines = lineLabels(selectedProfile);
  const selectedLocations = locationLabels(selectedProfile);
  const selectedEducationLabel = titleCase(selectedProfile?.educationLevel);
  const selectedVerified = talentVerified(selectedProfile);
  const selectedBackgroundChecked = Boolean(selectedProfile?.isBackgroundChecked);
  const selectedPhoto = talentPhoto(selectedProfile);
  const drawerProfile = selectedProfileDetails || {};
  const summary = drawerProfile.profileSummary || drawerProfile.description || drawerProfile.experience || selectedProfile?.summary || "";
  const candidateUserId = profileOwnerId(selectedProfileDetails) || profileOwnerId(selectedProfile);
  const canChat = Boolean(onChat && candidateUserId && candidateUserId !== actorId);

  return (
    <Drawer
      title="Talent profile"
      open={open}
      onClose={onClose}
      width="min(520px, 100vw)"
      className="olreadia-flow-drawer"
      destroyOnClose
    >
      {selectedProfile ? (
        <Space direction="vertical" size={14} className="olreadia-flow-drawer__stack">
          <Card className="olreadia-flow-card">
            <div className="olreadia-flow-card__top">
              <Avatar
                size={52}
                src={selectedPhoto || undefined}
                icon={!selectedPhoto ? <UserOutlined /> : undefined}
                style={{ background: OLIVEEB_COLORS.oliveebGreenSoft, color: OLIVEEB_COLORS.oliveebGreen }}
              />
              <div style={{ minWidth: 0 }}>
                <Title level={4}>{talentName(selectedProfile)}</Title>
                <Text type="secondary">{profileTitle(selectedProfile)}</Text>
              </div>
            </div>
            <Space wrap size={[6, 6]}>
              <Tag color={selectedVerified ? "green" : "default"} icon={<SafetyCertificateOutlined />}>
                {selectedVerified ? "Verified" : "Not verified"}
              </Tag>
              <Tag color={selectedBackgroundChecked ? "blue" : "default"} icon={<CheckCircleOutlined />}>
                {selectedBackgroundChecked ? "Background checked" : "No background check"}
              </Tag>
            </Space>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => onOpenProfileDetails?.(selectedProfile)}
              block
            >
              View more profile details
            </Button>
            {onChat || onShare ? (
              <Space.Compact style={{ width: "100%" }}>
                {onChat ? (
                  <Button
                    icon={<MessageOutlined />}
                    loading={chatStarting}
                    disabled={!canChat}
                    onClick={onChat}
                    style={{ width: onShare ? "50%" : "100%" }}
                  >
                    Chat with talent
                  </Button>
                ) : null}
                {onShare ? (
                  <Button
                    icon={<ShareAltOutlined />}
                    onClick={onShare}
                    style={{ width: onChat ? "50%" : "100%" }}
                  >
                    Share
                  </Button>
                ) : null}
              </Space.Compact>
            ) : null}
          </Card>

          <Card className="olreadia-flow-card">
            <Title level={4}>Profile Snapshot</Title>
            {detailsLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : null}
            {detailsError ? <Alert type="warning" showIcon message="Full profile details unavailable" description={detailsError} /> : null}
            {summary ? <Paragraph>{summary}</Paragraph> : null}
            <div className="olreadia-flow-detail-grid">
              {selectedLines.length ? (
                <div>
                  <Text><SolutionOutlined /> Profession</Text>
                  <strong>{selectedLines.join(", ")}</strong>
                </div>
              ) : null}
              {selectedLocations.length ? (
                <div>
                  <Text><EnvironmentOutlined /> Locations</Text>
                  <strong>{selectedLocations.join(", ")}</strong>
                </div>
              ) : null}
              {selectedEducationLabel ? (
                <div>
                  <Text><BookOutlined /> Education</Text>
                  <strong>{selectedEducationLabel}</strong>
                </div>
              ) : null}
              {drawerProfile?.profileType ? (
                <div>
                  <Text><UserOutlined /> Profile type</Text>
                  <strong>{titleCase(drawerProfile.profileType)}</strong>
                </div>
              ) : null}
              {drawerProfile?.isPrivate !== undefined ? (
                <div>
                  <Text><SafetyCertificateOutlined /> Visibility</Text>
                  <strong>{drawerProfile.isPrivate ? "Private" : "Visible"}</strong>
                </div>
              ) : null}
              {selectedProfile?.email ? (
                <div>
                  <Text><UserOutlined /> Contact</Text>
                  <strong>{selectedProfile.email}</strong>
                </div>
              ) : null}
            </div>
          </Card>

          {selectedProfileDetails ? (
            <>
              <ProfileDetailList
                title="Career"
                items={[
                  ...toList(drawerProfile.workExperienceHistory).map((item) => ({ item, label: "Experience" })),
                  ...toList(drawerProfile.education).map((item) => ({ item, label: "Education" })),
                  ...toList(drawerProfile.certifications).map((item) => ({ item, label: "Certification" })),
                  ...toList(drawerProfile.licenses).map((item) => ({ item, label: "License" })),
                ]}
              />
              <ProfileDetailList
                title="Readiness"
                items={[
                  ...toList(drawerProfile.previousRegionsWorkedIn).map((item) => ({ item, label: "Region" })),
                  ...toList(drawerProfile.references).map((item) => ({ item, label: "Reference" })),
                  ...toList(drawerProfile.documents).map((item) => ({ item, label: "Document" })),
                  ...toList(drawerProfile.photos).map((item) => ({ item, label: "Photo" })),
                ]}
              />
              <Card className="olreadia-flow-card">
                <Title level={4}>Services & Locations</Title>
                <Space wrap size={[6, 6]}>
                  {toList(drawerProfile.interestedServices).map((service, index) => (
                    <Tag key={`service-${index}`} color="green">{serviceLabel(service)}</Tag>
                  ))}
                  {toList(drawerProfile.interestedShifts).map((shift) => (
                    <Tag key={shift} color="blue">{titleCase(shift)}</Tag>
                  ))}
                </Space>
                <ProfileDetailRows
                  items={toList(drawerProfile.interestedLocations).map((item) => ({ item, label: "Location" }))}
                />
              </Card>
            </>
          ) : null}
        </Space>
      ) : null}
    </Drawer>
  );
};

const roleOptionsFor = (roles, query) => {
  const search = normalizeSearchValue(query);
  if (search.length < 2) return [];
  return roles
    .map((role) => {
      const label = String(role?.title || role || "").trim();
      const normalized = normalizeSearchValue(label);
      return { value: label, label, normalized, matchIndex: normalized.indexOf(search) };
    })
    .filter((item) => item.value && item.matchIndex >= 0)
    .sort((a, b) => {
      const exactA = a.normalized === search ? 0 : 1;
      const exactB = b.normalized === search ? 0 : 1;
      return exactA - exactB || a.matchIndex - b.matchIndex || a.normalized.length - b.normalized.length || a.value.localeCompare(b.value);
    })
    .slice(0, 8)
    .map(({ value, label }) => ({ value, label }));
};

const queryParam = (search, key) => new URLSearchParams(search).get(key);

export default function TalentSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const initialQuery = useMemo(() => {
    const q = queryParam(location.search, "q") || queryParam(location.search, "query") || "";
    const place = queryParam(location.search, "location") || "";
    return [q, place].filter(Boolean).join(" ").trim();
  }, [location.search]);
  const [query, setQuery] = useState(initialQuery);
  const [roles, setRoles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [error, setError] = useState("");
  const [rolesError, setRolesError] = useState("");
  const [hasSearched, setHasSearched] = useState(Boolean(initialQuery));
  const [lastPage, setLastPage] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedProfileDetails, setSelectedProfileDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [chatStarting, setChatStarting] = useState(false);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const queryRef = useRef(initialQuery);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const loadRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      setRolesError("");
      const nextRoles = await olreadiaService.professionRoles.list(actorId);
      setRoles(nextRoles);
    } catch (nextError) {
      setRoles([]);
      setRolesError(nextError?.message || "Could not load role suggestions.");
    } finally {
      setRolesLoading(false);
    }
  }, [actorId]);

  const loadProfiles = useCallback(async (reset = false, queryOverride) => {
    if (loadingRef.current) return;
    const keyword = queryOverride ?? queryRef.current;
    if (keyword.trim().length < 3) {
      setProfiles([]);
      setTotalResults(0);
      setHasSearched(false);
      setLastPage(true);
      pageRef.current = 0;
      setError("");
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setHasSearched(true);
      const nextPage = reset ? 0 : pageRef.current;
      const next = await olreadiaService.professionProfiles.searchTalent({
        actorId,
        query: keyword,
        page: nextPage,
        size: PAGE_SIZE,
      });
      const nextContent = next.content || [];
      setProfiles((current) => (reset ? nextContent : [...current, ...nextContent]));
      setTotalResults(next.totalElements ?? nextContent.length);
      setLastPage(
        (typeof next.totalPages === "number" && nextPage + 1 >= next.totalPages) ||
          nextContent.length < PAGE_SIZE,
      );
      pageRef.current = nextPage + 1;
      setError("");
    } catch (nextError) {
      if (reset) setProfiles([]);
      setError(nextError?.message || "Could not load talent profiles.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [actorId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    setQuery(initialQuery);
    if (initialQuery.trim()) {
      pageRef.current = 0;
      setLastPage(false);
      loadProfiles(true, initialQuery);
    }
  }, [initialQuery, loadProfiles]);

  const roleOptions = useMemo(() => roleOptionsFor(roles, query), [query, roles]);

  const runSearch = useCallback(() => {
    pageRef.current = 0;
    setLastPage(false);
    loadProfiles(true);
  }, [loadProfiles]);

  const selectRoleSuggestion = (role) => {
    setQuery(role);
    pageRef.current = 0;
    setLastPage(false);
    loadProfiles(true, role);
  };

  const clearFilters = () => {
    setQuery("");
    setProfiles([]);
    setTotalResults(0);
    setHasSearched(false);
    setLastPage(true);
    pageRef.current = 0;
    setError("");
  };

  const openProfileDetails = (profile) => {
    const profileId = profile?.professionProfileId;
    if (!profileId) return;
    navigate(paths.professionalProfileDetails(profileId));
  };

  const openProfilePreview = (profile) => {
    setSelectedProfile(profile);
    setSelectedProfileDetails(null);
    setDetailsError("");
  };

  const closeProfilePreview = () => {
    setSelectedProfile(null);
    setSelectedProfileDetails(null);
    setDetailsError("");
  };

  useEffect(() => {
    let cancelled = false;
    const profileId = selectedProfile?.professionProfileId;
    if (!profileId) return undefined;

    const loadDetails = async () => {
      try {
        setDetailsLoading(true);
        setDetailsError("");
        const result = await olreadiaService.professionProfiles.get(profileId);
        if (!cancelled) setSelectedProfileDetails(normalizeProfile(result));
      } catch (nextError) {
        if (!cancelled) setDetailsError(nextError?.message || "Could not load full profile details.");
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedProfile?.professionProfileId]);

  const candidateUserId = profileOwnerId(selectedProfileDetails) || profileOwnerId(selectedProfile);
  const profileShareUrl = selectedProfile?.professionProfileId
    ? `${window.location.origin}${paths.professionalProfileDetails(selectedProfile.professionProfileId)}`
    : "";

  const chatWithTalent = async () => {
    if (!candidateUserId) {
      message.warning("This talent profile does not include a chat-ready user ID.");
      return;
    }
    if (candidateUserId === actorId) {
      message.info("This is your own profile.");
      return;
    }
    try {
      setChatStarting(true);
      const conversation = await startPrivateConversation({
        actorId,
        participants: [candidateUserId],
      });
      const params = new URLSearchParams({
        participantId: candidateUserId,
        recipientName: talentName(selectedProfile),
      });
      if (conversation?.id) params.set("conversationId", conversation.id);
      navigate(`${paths.chatSpace}?${params.toString()}`);
    } catch (nextError) {
      message.error(nextError?.message || "Unable to start chat with this talent.");
    } finally {
      setChatStarting(false);
    }
  };

  const shareTalentProfile = async () => {
    if (!profileShareUrl) {
      message.warning("This profile is missing a shareable ID.");
      return;
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: talentName(selectedProfile),
          text: profileShareUrl,
          url: profileShareUrl,
        });
      } else {
        await navigator.clipboard.writeText(profileShareUrl);
        message.success("Profile link copied.");
      }
    } catch (nextError) {
      if (nextError?.name !== "AbortError") {
        message.error(nextError?.message || "Unable to share this profile right now.");
      }
    }
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><TeamOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Talent Search</Text>
              <Title level={2}>Search Talent</Title>
              <Paragraph>Find professional profiles by role, line of profession, education, and location.</Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading || rolesLoading} onClick={() => loadProfiles(true)}>Refresh</Button>
          </section>

          <Card className="olreadia-flow-card" style={{ marginBottom: 12 }}>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 10 }}>
                <AutoComplete
                  allowClear
                  value={query}
                  options={roleOptions}
                  onChange={setQuery}
                  onSelect={selectRoleSuggestion}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") runSearch();
                  }}
                  placeholder="Search e.g. nurse, caregiver, CPR, Los Angeles"
                  style={{ width: "100%" }}
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={runSearch} loading={loading} block>
                  Search
                </Button>
                <Button onClick={clearFilters} block>Clear</Button>
              </div>
              <Space wrap size={[8, 6]}>
                <Button icon={<FilterOutlined />} disabled>
                  Advanced Search
                </Button>
                {rolesLoading ? <Tag color="blue">Loading role suggestions</Tag> : null}
                {rolesError ? <Tag color="gold">{rolesError}</Tag> : null}
                {query.trim().length > 0 && query.trim().length < 3 ? (
                  <Tag color="gold">Enter at least 3 characters to search</Tag>
                ) : null}
              </Space>
            </Space>
          </Card>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load talent"
              description={error}
              action={<Button size="small" onClick={() => loadProfiles(true)}>Retry</Button>}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {hasSearched ? (
            <Text style={{ display: "block", marginBottom: 10, color: OLIVEEB_COLORS.oliveebDarkBlue, fontWeight: 800 }}>
              {loading && profiles.length === 0
                ? "Loading..."
                : `${totalResults || profiles.length} talent profile${(totalResults || profiles.length) === 1 ? "" : "s"} found`}
            </Text>
          ) : null}

          {loading && profiles.length === 0 ? (
            <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 6 }} /></Card>
          ) : null}

          {!loading && !profiles.length ? (
            <Card className="olreadia-flow-card">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={hasSearched ? "No talent found" : "Search for talent"}
              >
                <Text type="secondary">
                  {hasSearched
                    ? "Try a broader search string or adjust the search term."
                    : "Start typing a professional role or choose a suggestion."}
                </Text>
              </Empty>
            </Card>
          ) : null}

          {profiles.length ? (
            <List
              itemLayout="vertical"
              dataSource={profiles}
              renderItem={(profile) => {
                const lines = lineLabels(profile);
                const locations = locationLabels(profile);
                const educationLabel = titleCase(profile.educationLevel);
                const verified = talentVerified(profile);
                const backgroundChecked = Boolean(profile.isBackgroundChecked);
                const photo = talentPhoto(profile);
                return (
                  <Card
                    key={profile.professionProfileId || profile.userProfileId}
                    className="olreadia-flow-card"
                    hoverable
                    style={{ marginBottom: 10 }}
                    onClick={() => openProfilePreview(profile)}
                  >
                    <div className="olreadia-flow-card__top">
                      <Avatar
                        size={44}
                        src={photo || undefined}
                        icon={!photo ? <UserOutlined /> : undefined}
                        style={{ background: OLIVEEB_COLORS.oliveebGreenSoft, color: OLIVEEB_COLORS.oliveebGreen }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Title level={4} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                          {talentName(profile)}
                        </Title>
                        <Text type="secondary">{profileTitle(profile)}</Text>
                      </div>
                      <Button
                        icon={<EyeOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          openProfilePreview(profile);
                        }}
                      >
                        View details
                      </Button>
                    </div>

                    <Space wrap size={[6, 6]} style={{ marginTop: 10 }}>
                      <Tag color={verified ? "green" : "default"} icon={<SafetyCertificateOutlined />}>
                        {verified ? "Verified" : "Not verified"}
                      </Tag>
                      <Tag color={backgroundChecked ? "blue" : "default"} icon={<CheckCircleOutlined />}>
                        {backgroundChecked ? "Background checked" : "No background check"}
                      </Tag>
                    </Space>

                    <div className="olreadia-flow-detail-grid" style={{ marginTop: 12 }}>
                      {lines.length ? (
                        <div>
                          <Text><SolutionOutlined /> Profession</Text>
                          <strong>{lines.join(", ")}</strong>
                        </div>
                      ) : null}
                      {locations.length ? (
                        <div>
                          <Text><EnvironmentOutlined /> Locations</Text>
                          <strong>{locations.join(", ")}</strong>
                        </div>
                      ) : null}
                      {educationLabel ? (
                        <div>
                          <Text><BookOutlined /> Education</Text>
                          <strong>{educationLabel}</strong>
                        </div>
                      ) : null}
                    </div>
                  </Card>
                );
              }}
            />
          ) : null}

          {profiles.length && !lastPage ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <Button type="primary" loading={loading} onClick={() => loadProfiles(false)}>
                Load more
              </Button>
            </div>
          ) : null}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>
      <TalentProfileDrawer
        actorId={actorId}
        chatStarting={chatStarting}
        detailsError={detailsError}
        detailsLoading={detailsLoading}
        onChat={chatWithTalent}
        onClose={closeProfilePreview}
        onOpenProfileDetails={openProfileDetails}
        onShare={shareTalentProfile}
        open={Boolean(selectedProfile)}
        selectedProfile={selectedProfile}
        selectedProfileDetails={selectedProfileDetails}
      />
    </div>
  );
}

function ProfileDetailList({ title, items }) {
  const visibleItems = items.filter(({ item }) => {
    if (typeof item === "string") return item.trim();
    return item && Object.keys(item).length;
  });

  return (
    <Card className="olreadia-flow-card">
      <Title level={4}>{title}</Title>
      <ProfileDetailRows items={visibleItems} />
    </Card>
  );
}

function ProfileDetailRows({ items }) {
  if (!items.length) return <Text type="secondary">No details added yet.</Text>;

  return (
    <div className="olreadia-flow-nested-list">
      {items.map(({ item, label }, index) => (
        <div key={`${label}-${index}`} className="olreadia-flow-nested-row">
          <span><IdcardDrawerIcon label={label} /></span>
          <span>
            <strong>{itemTitle(item, label)}</strong>
            {itemMeta(item) ? <Text>{itemMeta(item)}</Text> : <Text type="secondary">{label}</Text>}
          </span>
          <Tag>{label}</Tag>
        </div>
      ))}
    </div>
  );
}

function IdcardDrawerIcon({ label }) {
  const normalized = String(label || "").toLowerCase();
  if (normalized.includes("education")) return <BookOutlined />;
  if (normalized.includes("location") || normalized.includes("region")) return <EnvironmentOutlined />;
  if (normalized.includes("certification")) return <SafetyCertificateOutlined />;
  return <SolutionOutlined />;
}
